import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '@/lib/api-client';

export interface WeatherData {
  location: string;
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  lastUpdated: string;
}

export interface WeatherWarning {
  id: string;
  ticketId: string;
  projectId: string;
  riskLevel: 'low' | 'medium' | 'high';
  workType: string;
  recommendation: string;
  smartWarningText?: string;
  isActive: boolean;
  reasons: string[];
  warningLevel: 'info' | 'warning' | 'critical';
  userAcknowledged: boolean;
  lastChecked: string;
  createdAt: string;
}

export interface ProjectWeatherData {
  projectId: string;
  projectName: string;
  location: string;
  weather: WeatherData | null;
  warnings: WeatherWarning[];
  lastUpdated: string;
}

interface WeatherStore {
  // Project-specific weather data (using plain object instead of Map for serialization)
  projectWeather: Record<string, ProjectWeatherData>;
  projectWeatherLoading: boolean;
  projectWeatherError: string | null;
  
  // Weather warnings
  allWarnings: WeatherWarning[];
  warningsLoading: boolean;
  warningsError: string | null;
  
  // Actions
  fetchProjectWeather: (projectId: string, retryCount?: number) => Promise<void>;
  fetchAllWarnings: () => Promise<void>;
  getProjectWeather: (projectId: string) => ProjectWeatherData | null;
  getProjectWarnings: (projectId: string) => WeatherWarning[];
  getTicketWarnings: (ticketId: string, projectId: string) => WeatherWarning[];
  isProjectWeatherStale: (projectId: string) => boolean;
  forceRefreshWeather: (projectId: string) => Promise<void>;
  clearWeatherData: () => void;
}

export const useWeatherStore = create<WeatherStore>()(
  persist(
    (set, get) => ({
      // Initial state
      projectWeather: {},
      projectWeatherLoading: false,
      projectWeatherError: null,
      
      allWarnings: [],
      warningsLoading: false,
      warningsError: null,
      
      // Fetch weather data for a specific project with freshness checking
      fetchProjectWeather: async (projectId, retryCount = 0) => {
        // Check if we already have fresh weather data (less than 1 hour old)
        const existingProjectWeather = get().projectWeather[projectId];
        if (existingProjectWeather && existingProjectWeather.weather) {
          const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
          const lastUpdated = new Date(existingProjectWeather.lastUpdated);
          
          if (lastUpdated > oneHourAgo) {
            return; // Use existing fresh data
          }
        }
        
        set({ projectWeatherLoading: true, projectWeatherError: null });
        
        try {
          // Fetch project data first to get project name and location info
          const projectResult = await apiClient.customRequest(`/api/projects/${projectId}`);
          
          if (!projectResult || !(projectResult as any).success) {
            // If project not found and this is a retry attempt, throw error
            if (retryCount > 0) {
              throw new Error('Failed to fetch project data');
            }
            // For newly created projects, retry after a delay
            setTimeout(() => {
              get().fetchProjectWeather(projectId, retryCount + 1);
            }, 500 * (retryCount + 1)); // Exponential backoff: 500ms, 1000ms, 1500ms
            return;
          }
          
          const project = (projectResult as any).data;
          const projectName = project.name || 'Unknown Project';
          const projectLocation = project.city 
            ? `${project.city}${project.country ? `, ${project.country}` : ''}`
            : project.country || 'Unknown';
          
          // Verify project has address or coordinates before fetching weather
          const hasAddress = project.street || project.city || project.postalCode || project.country;
          const hasCoordinates = project.latitude && project.longitude;
          
          if (!hasAddress && !hasCoordinates) {
            // Project doesn't have location data yet - retry if this is first attempt
            if (retryCount < 3) {
              setTimeout(() => {
                get().fetchProjectWeather(projectId, retryCount + 1);
              }, 500 * (retryCount + 1));
              return;
            }
            // After 3 retries, give up and store null weather
            const projectData: ProjectWeatherData = {
              projectId,
              projectName,
              location: projectLocation,
              weather: null,
              warnings: get().getProjectWarnings(projectId),
              lastUpdated: new Date().toISOString()
            };
            set(state => ({
              projectWeather: {
                ...state.projectWeather,
                [projectId]: projectData
              },
              projectWeatherLoading: false,
              projectWeatherError: 'Project location data not available'
            }));
            return;
          }
          
          // Fetch weather using projectId - API will handle geocoding
          const result = await apiClient.customRequest('/api/weather', {
            method: 'POST',
            body: JSON.stringify({
              projectId: projectId
            })
          });
          
          if (result && (result as any).success) {
            const apiData = (result as any).data;
            const transformedData = {
              location: apiData.location,
              temperature: parseFloat(apiData.temperature.replace('°C', '')),
              condition: apiData.conditions,
              humidity: 0, // API doesn't provide humidity, using 0 as fallback
              windSpeed: parseFloat(apiData.wind.split(' ')[0]),
              lastUpdated: apiData.timestamp ? new Date(apiData.timestamp).toISOString() : new Date().toISOString()
            };
            
            const projectData: ProjectWeatherData = {
              projectId,
              projectName,
              location: projectLocation,
              weather: transformedData,
              warnings: get().getProjectWarnings(projectId),
              lastUpdated: new Date().toISOString()
            };
            
            set(state => ({
              projectWeather: {
                ...state.projectWeather,
                [projectId]: projectData
              },
              projectWeatherLoading: false
            }));
          } else {
            // If weather fetch failed, retry for newly created projects (up to 3 times)
            if (retryCount < 3) {
              const errorMessage = (result as any)?.message || 'Failed to fetch project weather data';
              // Check if error suggests project might not be ready yet
              const isProjectNotReady = errorMessage.includes('not found') || 
                                      errorMessage.includes('cannot be pinpointed') ||
                                      errorMessage.includes('No coordinates available');
              
              if (isProjectNotReady) {
                // Retry with exponential backoff
                setTimeout(() => {
                  get().fetchProjectWeather(projectId, retryCount + 1);
                }, 500 * (retryCount + 1));
                return;
              }
            }
            
            // After retries exhausted or non-retryable error, store null weather
            const projectData: ProjectWeatherData = {
              projectId,
              projectName,
              location: projectLocation,
              weather: null,
              warnings: get().getProjectWarnings(projectId),
              lastUpdated: new Date().toISOString()
            };
            
            set(state => ({
              projectWeather: {
                ...state.projectWeather,
                [projectId]: projectData
              },
              projectWeatherLoading: false,
              projectWeatherError: (result as any)?.message || 'Failed to fetch project weather data'
            }));
          }
        } catch (error) {
          // Retry on network/API errors for newly created projects
          if (retryCount < 3 && error instanceof Error) {
            const errorMessage = error.message.toLowerCase();
            const isRetryableError = errorMessage.includes('failed') || 
                                    errorMessage.includes('network') ||
                                    errorMessage.includes('timeout');
            
            if (isRetryableError) {
              setTimeout(() => {
                get().fetchProjectWeather(projectId, retryCount + 1);
              }, 500 * (retryCount + 1));
              return;
            }
          }
          
          set({ 
            projectWeatherError: error instanceof Error ? error.message : 'Unknown error',
            projectWeatherLoading: false 
          });
        }
      },
      
      // Fetch all weather warnings
      fetchAllWarnings: async () => {
        set({ warningsLoading: true, warningsError: null });
        
        try {
          const result = await apiClient.customRequest('/api/weather/warnings');
          
          if (result && (result as any).success && Array.isArray((result as any).data)) {
            set({ 
              allWarnings: (result as any).data,
              warningsLoading: false 
            });
            //console.log('🌤️ All weather warnings fetched:', result.data.length);
          } else {
            set({ 
              allWarnings: [],
              warningsLoading: false 
            });
          }
        } catch (error) {
          //console.error('🌤️ Weather warnings fetch error:', error);
          set({ 
            warningsError: error instanceof Error ? error.message : 'Unknown error',
            warningsLoading: false 
          });
        }
      },
      
      // Get weather data for a specific project
      getProjectWeather: (projectId) => {
        const projectWeather = get().projectWeather[projectId] || null;
        return projectWeather;
      },
      
      // Get warnings for a specific project
      getProjectWarnings: (projectId) => {
        return get().allWarnings.filter(warning => 
          warning.projectId === projectId && warning.isActive
        );
      },
      
      // Get warnings for a specific ticket
      getTicketWarnings: (ticketId, projectId) => {
        return get().allWarnings.filter(warning => 
          warning.ticketId === ticketId && warning.projectId === projectId && warning.isActive
        );
      },
      
      // Check if project weather is stale (older than 1 hour)
      isProjectWeatherStale: (projectId: string) => {
        const projectWeather = get().projectWeather[projectId];
        if (!projectWeather) {
          return true; // No weather data = stale
        }
        
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const lastUpdated = new Date(projectWeather.lastUpdated);
        
        // Also check if the date seems wrong (like September 2025)
        const now = new Date();
        if (lastUpdated > now) {
          return true;
        }
        
        return lastUpdated < oneHourAgo;
      },

      // Force refresh weather for a specific project
      forceRefreshWeather: async (projectId: string) => {
        // Clear cache for this project only
        set(state => {
          const newProjectWeather = { ...state.projectWeather };
          delete newProjectWeather[projectId];
          return {
            projectWeather: newProjectWeather,
            projectWeatherLoading: true,
            projectWeatherError: null
          };
        });
        
        try {
          // Fetch project data first to get project name and location info
          const projectResult = await apiClient.customRequest(`/api/projects/${projectId}`);
          
          if (!projectResult || !(projectResult as any).success) {
            throw new Error('Failed to fetch project data');
          }
          
          const project = (projectResult as any).data;
          const projectName = project.name || 'Unknown Project';
          const projectLocation = project.city 
            ? `${project.city}${project.country ? `, ${project.country}` : ''}`
            : project.country || 'Unknown';
          
          // Fetch weather using projectId - API will handle geocoding
          const result = await apiClient.customRequest('/api/weather', {
            method: 'POST',
            body: JSON.stringify({
              projectId: projectId
            })
          });
          
          if (result && (result as any).success) {
            const apiData = (result as any).data;
            const transformedData = {
              location: apiData.location,
              temperature: parseFloat(apiData.temperature.replace('°C', '')),
              condition: apiData.conditions,
              humidity: 0,
              windSpeed: parseFloat(apiData.wind.split(' ')[0]),
              lastUpdated: apiData.timestamp ? new Date(apiData.timestamp).toISOString() : new Date().toISOString()
            };
            
            const projectData: ProjectWeatherData = {
              projectId,
              projectName,
              location: projectLocation,
              weather: transformedData,
              warnings: get().getProjectWarnings(projectId),
              lastUpdated: new Date().toISOString()
            };
            
            set(state => ({
              projectWeather: {
                ...state.projectWeather,
                [projectId]: projectData
              },
              projectWeatherLoading: false
            }));
          } else {
            throw new Error((result as any)?.message || 'Failed to fetch project weather data');
          }
        } catch (error) {
          set({ 
            projectWeatherError: error instanceof Error ? error.message : 'Unknown error',
            projectWeatherLoading: false 
          });
        }
      },

      // Clear all weather data
      clearWeatherData: () => {
        set({
          projectWeather: {},
          allWarnings: [],
          projectWeatherLoading: false,
          warningsLoading: false,
          projectWeatherError: null,
          warningsError: null
        });
      }
    }),
    {
      name: 'weather-store',
      // Only persist essential data, not loading states
      partialize: (state) => ({
        projectWeather: state.projectWeather,
        allWarnings: state.allWarnings
      })
    }
  )
);
